import React from 'react';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Asset } from 'contentful';
import cx from 'classnames';

import { IHomepageHeroFields } from '../../../types/contentful';

import { CFColorTokenProp } from '../../theme/tokens/contentful-color';

import { getImageData } from '../../utils/contentful';

import { PopUp } from './PopUp';
import { Eyebrow } from '../Eyebrow';
import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';
import CtaWidget from '../CtaWidget';
import {
  ctaWidgetEnum,
  DEFAULT_LABEL,
  DEFAULT_BUTTON_TEXT
} from '../CtaWidget/shared';
import GlImage from '../Image';

export function HomepageHero({
  headingText,
  useLightHeadingText,
  subheadingText,
  useLightSubheadingText,
  mobileSubheadingText,
  mobileHeadingText,
  image,
  desktopImage,
  fullWidthDesktopImage,
  backgroundColor,
  desktopBackgroundColor,
  desktopSubheadingSize,
  mobileSubheadingSize,
  ctaText,
  ctaColor,
  ctaHoverColor,
  ctaDesktopColor,
  ctaDesktopHoverColor,
  eyebrow,
  privacyPolicyDisclaimer
}: IHomepageHeroFields) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p
          className={cx(
            'max-w-[272px] text-center',
            'tablet:min-w-[260px] tablet:max-w-[320px]',
            'desktop:min-w-[260px] desktop:max-w-[424px] desktop:text-left',
            'text-[12px] tablet:text-sm',
            'font-normal',
            'text-black',
            useLightHeadingText
              ? 'desktop:text-white'
              : 'desktop:text-black'
          )}
        >
          {children}
        </p>
      );
    },
    [INLINES.HYPERLINK]: (node, children) => (
      <a
        href={node.data.uri}
        className={cx(
          'text-black font-normal underline transition-colors duration-200 whitespace-nowrap',
          'hover:text-navy',
          'desktop:hover:text-green',
          useLightHeadingText
            ? 'desktop:text-white desktop:hover:text-green'
            : 'desktop:text-black desktop:hover:text-navy'
        )}
      >
        {children}
      </a>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode
        node={node}
        colour={{
          0: 'defaultText',
          desktop: useLightHeadingText ? 'white' : 'defaultText'
        }}
        fontWeight="p"
      >
        {children}
      </EmbeddedInlineEntryNode>
    )
  };

  const DesktopHeroContents = () => {
    return (
      <div className="w-10/12 flex">
        <div className="flex flex-col justify-center flex-[0_0_28rem]">
          {eyebrow && (
            <div className="mb-4 mt-12">
              <div className="w-11/12 tablet:w-2/3">
                <Eyebrow eyebrowFields={eyebrow.fields} />
              </div>
            </div>
          )}
          <div className={cx('mb-4', eyebrow ? 'mt-4' : 'mt-16')}>
            <h1
              className={cx(
                'text-left',
                'text-[40px] tablet:text-[50px]',
                'font-medium',
                useLightHeadingText ? 'text-white' : 'text-black'
              )}
              data-elementid="homepage-hero-headline"
              data-testid="homepage-hero-headline"
              dangerouslySetInnerHTML={{
                __html: headingText || ''
              }}
            />
          </div>
          <div className="mb-5 max-w-[28rem]">
            <h2
              className={cx(
                'm-0',
                'text-left',
                'font-normal',
                'leading-[1.125]',
                'font-glRepro',
                useLightSubheadingText ? 'text-white' : 'text-black'
              )}
              style={{
                fontSize: desktopSubheadingSize ? `${desktopSubheadingSize}rem` : '1.75rem',
                lineHeight: '1.125'
              }}
              data-elementid="homepage-hero-subhead"
            >
              {subheadingText}
            </h2>
          </div>
          <div className="mb-16">
            <div className="flex flex-col">
              <div
                className="flex flex-grow max-w-[272px] justify-center tablet:min-w-[260px] tablet:max-w-[320px] desktop:min-w-[260px] desktop:max-w-[422px]"
                data-elementid="phone-number-wrapper-desktop"
                style={{ flexFlow: 'wrap' }}
              >
                <CtaWidget
                  id="homepage-hero-phone-number-desktop"
                  module="Hero"
                  errorPosition="bottom"
                  width="100%"
                  sysId="legacyCta"
                  designType={ctaWidgetEnum.MOBILE_PHONE_NUMBER}
                  placeholder={DEFAULT_LABEL}
                  label={DEFAULT_LABEL}
                  cfButtonColor={
                    (ctaDesktopColor as CFColorTokenProp) || 'green'
                  }
                  cfButtonHoverColor={
                    (ctaDesktopHoverColor as CFColorTokenProp) || 'sweetmint'
                  }
                  buttonContent={ctaText || DEFAULT_BUTTON_TEXT}
                  data-testid="homepage-hero-phone-number-desktop"
                />
              </div>
              {privacyPolicyDisclaimer && (
                <div className="mt-5">
                  <RichText
                    data={privacyPolicyDisclaimer}
                    overrideOptions={RICH_TEXT_OVERRIDE}
                  />
                </div>
              )}
              <PopUp />
            </div>
          </div>
        </div>
        <div className="flex-1 pt-4 self-start">
          <GlImage
            {...getImageData((desktopImage as Asset) || {})}
            className={cx(
              'h-full',
              fullWidthDesktopImage?.fields ? 'hidden' : 'visible'
            )}
            loading="eager"
            heroImage={true}
          />
        </div>
      </div>
    );
  };

  const bgColor = backgroundColor || 'F4BA42';
  const desktopBgColor = desktopBackgroundColor || backgroundColor || 'F4BA42';

  return (
    <>
      {/* Mobile Version */}
      <div
        className="block desktop:hidden"
        data-blockid="homepage-hero-mobile"
        data-testid="homepage-hero-mobile"
      >
        <section className="block-container">
          <div className="layout tiny:px-2 tablet:px-12" style={{ margin: 0 }}>
            <div className="w-full">
              <div
                className={cx(
                  'relative flex-1 rounded-[64px]',
                  'h-[500px]',
                  'tablet:h-[750px]'
                )}
                style={{
                  backgroundColor: `#${bgColor}`
                }}
                data-elementid="homepage-hero-mobile-background"
              >
                <div
                  className={cx(
                    'absolute z-[1] bottom-5 left-4 right-4',
                    'tablet:bottom-10 tablet:left-10 tablet:right-10',
                    'bg-green rounded-[40px]',
                    'p-4',
                    'tablet:py-5 tablet:px-8'
                  )}
                >
                  <h1
                    className={cx(
                      'max-w-[225px] mx-auto pb-4',
                      'tablet:max-w-[512px] tablet:pb-5',
                      'text-center font-medium text-black',
                      'leading-[1.125]'
                    )}
                    style={{
                      fontSize: `${mobileSubheadingSize || '1.75'}rem`
                    }}
                    data-elementid="homepage-hero-headline"
                  >
                    {(mobileHeadingText || headingText || '').replace(
                      /<br>/g,
                      ' '
                    )}
                  </h1>
                  <div className="flex flex-col mx-auto justify-center items-center">
                    <div
                      className="flex flex-grow max-w-[272px] justify-center tablet:min-w-[260px] tablet:max-w-[320px] desktop:min-w-[260px] desktop:max-w-[422px]"
                      data-elementid="phone-wrapper-mobile"
                    >
                      <CtaWidget
                        id="homepage-hero-phone-number-mobile"
                        module="Hero"
                        errorPosition="bottom"
                        width="100%"
                        sysId="legacyCta"
                        designType={ctaWidgetEnum.MOBILE_PHONE_NUMBER}
                        placeholder={DEFAULT_LABEL}
                        label={DEFAULT_LABEL}
                        cfButtonColor={
                          (ctaColor as CFColorTokenProp) || 'ginger'
                        }
                        cfButtonHoverColor={
                          (ctaHoverColor as CFColorTokenProp) || 'sweetmint'
                        }
                        buttonContent={ctaText || DEFAULT_BUTTON_TEXT}
                        data-testid="mobilePhoneWidget"
                        data-elementid="phone-widget-mobile"
                      />
                    </div>
                    {privacyPolicyDisclaimer && (
                      <div className="mt-4 mx-3">
                        <RichText
                          data={privacyPolicyDisclaimer}
                          overrideOptions={RICH_TEXT_OVERRIDE}
                        />
                      </div>
                    )}
                    <PopUp />
                  </div>
                </div>
                <div className="overflow-hidden inline-block absolute h-full w-full rounded-[64px]">
                  <div style={{ height: '100%', position: 'relative' }}>
                    <GlImage
                      {...getImageData(image)}
                      loading="eager"
                      heroImage={true}
                      layout="fill"
                      objectFit="cover"
                      width={undefined}
                      height={undefined}
                      className="h-full rounded-[56px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section
          className={cx(
            'block-container',
            'mt-4 tablet:mt-6',
            eyebrow ? 'mb-4' : 'mb-4 tablet:mb-12'
          )}
        >
          <div className="layout layout-padding">
            <div className="w-11/12 tablet:w-2/3">
              <h2
                className={cx(
                  'text-center text-black font-h2',
                  'text-[28px] tablet:text-[42px]'
                )}
                data-elementid="homepage-hero-subhead"
              >
                {mobileSubheadingText || subheadingText}
              </h2>
            </div>
          </div>
        </section>
        {eyebrow && (
          <section className="block-container mt-6 mb-4 tablet:mb-6">
            <div className="w-11/12">
              <Eyebrow data-testid="eyebrow" eyebrowFields={eyebrow.fields} />
            </div>
          </section>
        )}
      </div>

      {/* Desktop Version */}
      <section
        className="desktop-block-container mt-9 mb-9 tablet:mt-3 tablet:mb-10"
        data-blockid="homepage-hero-desktop"
        data-e2e="hero"
        style={{
          backgroundColor: `#${desktopBgColor}`
        }}
      >
        <div className="grid relative w-full max-w-screen-max mx-15">
          <div className="[grid-area:1/1]">
            <GlImage
              {...getImageData(fullWidthDesktopImage as Asset)}
              loading="eager"
              heroImage={true}
              className="[grid-area:1/1] rounded-[56px] desktop:rounded-[104px] z-0 w-full"
            />
          </div>
          <div className="[grid-area:1/1] relative place-items-center grid">
            <DesktopHeroContents />
          </div>
        </div>
      </section>
    </>
  );
}
